// Pure, framework-free rollup of the period-grain eligibility read into the franchise-grain
// triage model (design §5). The read returns one row per stored period; a franchise with no
// configuration arrives as a single synthetic row (enablement_period_id === null,
// eligibility_state === 'unconfigured') that fails open.
//
// Each period is classified by the SERVER-DERIVED `eligibility_state`, which is authoritative and
// type-safe. (Re-deriving from `is_active` is a trap: it is a SQL BIT that arrives as a JS boolean
// `true`/`false`, not `1`.) The server's CASE already folds is_active/review_status into:
//   'confirmed'                 => active + confirmed  (participates in the Audit)
//   'proposed' | 'manual_review'=> active proposal     (does not participate; needs review)
//   'rejected' | 'inactive'     => non-participating
//   'unconfigured'              => no stored period     (fail-open)

const isReal = (p) => p.enablement_period_id !== null && p.enablement_period_id !== undefined;
const isConfirmed = (p) => p.eligibility_state === "confirmed";
const isProposal = (p) =>
  p.eligibility_state === "proposed" || p.eligibility_state === "manual_review";
// Half-open interval [from, through): open-ended when `through` is absent (NULL -> null over JSON).
const isOpenEnded = (p) =>
  p.enabled_through_date === null ||
  p.enabled_through_date === undefined ||
  p.enabled_through_date === "";

// Per-franchise summary derived from that franchise's period rows (design §5). PURE and SHARED:
// the fleet rollup (rollupFranchises) calls it per com_code, and the future single-franchise
// Franchise Details view can call it directly with one franchise's periods — neither reimplements
// the derivation. Accepts the franchise's rows; a synthetic unconfigured placeholder is ignored.
//
// The "configuration summary" (configState/configLabel) is a DERIVED v1 rollup; "attention reasons"
// are SEPARATE and ORTHOGONAL (a confirmed franchise may still carry them). In this read-only-plus-
// reject stage attention is derived only from pending proposals; §7 evidence anomalies come later.
export function summarizeFranchise(periods = []) {
  const realPeriods = periods.filter(isReal);
  const confirmed = realPeriods.filter(isConfirmed);
  const proposals = realPeriods.filter(isProposal);

  const attention = proposals.map((p) => ({
    period_id: p.enablement_period_id,
    kind: p.eligibility_state === "manual_review" ? "manual_review" : "proposed",
    label: p.eligibility_state === "manual_review" ? "Manual review" : "Awaiting review",
    note: p.evidence_note || null,
  }));

  // A franchise with no ACTIVE governing period (confirmed or proposed) fails open: it is governed
  // by the current LK/SI flag. That covers unconfigured, rejected-only, and inactive-only alike.
  let configState;
  let configLabel;
  if (confirmed.length > 0) {
    // configState stays 'confirmed' for both open and all-closed (an all-closed franchise is still
    // confirmed, so it counts in the "Confirmed" summary chip); only the label distinguishes them.
    configState = "confirmed";
    configLabel = confirmed.some(isOpenEnded) ? "Confirmed" : "Closed";
  } else if (proposals.length > 0) {
    configState = "needs_review";
    configLabel = "Needs review";
  } else {
    configState = "fail_open";
    configLabel = "Fail-open";
  }

  const openConfirmedCount = confirmed.filter(isOpenEnded).length;
  const updatedAts = realPeriods.map((p) => p.updated_at).filter(Boolean).sort();

  return {
    periods: realPeriods,
    periodCount: realPeriods.length,
    confirmedCount: confirmed.length,
    openConfirmedCount,
    closedConfirmedCount: confirmed.length - openConfirmedCount,
    proposalCount: proposals.length,
    configState,
    configLabel,
    attention,
    lastUpdated: updatedAts.length ? updatedAts[updatedAts.length - 1] : null,
  };
}

// Franchise-grain rollup for the fleet-wide triage grid. One object per com_code (franchise
// identity + the shared per-franchise summary), sorted attention-first then by code.
export function rollupFranchises(periods = []) {
  const byCom = new Map();
  for (const p of periods) {
    if (!byCom.has(p.com_code)) byCom.set(p.com_code, []);
    byCom.get(p.com_code).push(p);
  }

  const franchises = [];
  for (const [com_code, rows] of byCom.entries()) {
    const first = rows[0] || {};
    franchises.push({
      com_code,
      franchise_name: first.franchise_name || com_code,
      dim_franchise_id: first.dim_franchise_id ?? null,
      islilkickerclient: first.islilkickerclient ?? null,
      ...summarizeFranchise(rows),
    });
  }

  franchises.sort((a, b) => {
    const aa = a.attention.length > 0 ? 0 : 1;
    const bb = b.attention.length > 0 ? 0 : 1;
    if (aa !== bb) return aa - bb;
    return String(a.com_code).localeCompare(String(b.com_code));
  });

  return franchises;
}

// Franchise-grain summary chips (design §5: Confirmed / Needs review / Fail-open).
export function summarize(franchises = []) {
  return {
    total: franchises.length,
    confirmed: franchises.filter((f) => f.configState === "confirmed").length,
    needsReview: franchises.filter((f) => f.attention.length > 0).length,
    failOpen: franchises.filter((f) => f.configState === "fail_open").length,
  };
}

// Ordered timeline entries for one franchise's periods: every stored period plus DERIVED inactive
// gaps between consecutive confirmed periods. A gap is the half-open span [prev.through, next.from);
// it is never stored (design §3.2, §9.1a-2). Entries are ordered by start date (ISO strings sort
// chronologically) so gaps fall naturally between the periods they separate.
export function buildTimeline(periods = []) {
  const entries = periods.map((p) => ({
    type: "period",
    sortKey: String(p.enabled_from_date),
    period: p,
  }));

  const conf = periods
    .filter(isConfirmed)
    .sort((a, b) => String(a.enabled_from_date).localeCompare(String(b.enabled_from_date)));
  for (let i = 1; i < conf.length; i++) {
    const prev = conf[i - 1];
    const next = conf[i];
    if (!isOpenEnded(prev) &&
        String(next.enabled_from_date) > String(prev.enabled_through_date)) {
      entries.push({
        type: "gap",
        sortKey: String(prev.enabled_through_date),
        from: prev.enabled_through_date,
        through: next.enabled_from_date,
      });
    }
  }

  entries.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return entries;
}
